import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { intakeQuestionnaireActions } from '../../../../store';
import { getUtcOffset } from '../../../../utils/common';
import { CONCLUDE_REQUIRED_INTAKE_TAB_IDS, INTAKE_TAB_CONFIGS } from '../constants';
import useIntakeTabStatuses from './useIntakeTabStatuses';
import useIsIntakeSaving from './useIsIntakeSaving';

import type { AppDispatch, RootState } from '../../../../store';

export default function useConcludeIntakeQuestionnaire() {
  const dispatch = useDispatch<AppDispatch>();
  const { intakeQuestionnaireId } = useParams<'intakeQuestionnaireId'>();
  const { getAccessTokenSilently } = useAuth0();
  const isSubmitting = useSelector(
    (state: RootState) => state.intakeQuestionnaire.submitIntakeQuestionnaireLoading,
  );
  const isSaving = useIsIntakeSaving();
  const tabStatuses = useIntakeTabStatuses();

  // Labels of the required pages whose schema-derived status isn't yet complete, in tab order.
  const errorPageLabels = INTAKE_TAB_CONFIGS.filter(
    (tab) => CONCLUDE_REQUIRED_INTAKE_TAB_IDS.includes(tab.id) && !tabStatuses[tab.id].success,
  ).map((tab) => tab.label);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  const confirmConclude = async () => {
    // Never conclude while a field save is still in flight — it could otherwise
    // reach the server first and leave out the last change.
    if (!intakeQuestionnaireId || isSaving) return;

    const result = await dispatch(
      intakeQuestionnaireActions.submitIntakeQuestionnaire({
        intakeQuestionnaireId,
        data: {
          date_concluded_utc_offset: getUtcOffset(moment()),
        },
        getAccessTokenSilently,
      }),
    );

    if (intakeQuestionnaireActions.submitIntakeQuestionnaire.fulfilled.match(result)) {
      closeDialog();
    }
  };

  return {
    isDialogOpen,
    isSubmitting,
    isSaving,
    errorPageLabels,
    openDialog,
    closeDialog,
    confirmConclude,
  };
}
